import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { DimensionKey, loadDraftAssist } from '../../../../queries/recognition-analysis';
import { cleanedDraftLength } from '../utils';

const MIN_CLEANED_DRAFT_LENGTH = 30;

const NOOP = () => {};

/** Maximum number of suggestions surfaced per Give Box open (session guard). */
export const MAX_SUGGESTIONS_PER_SESSION = 3;

/** Minimum milliseconds of typing inactivity before suggestions or the hint bubble may appear. */
const TYPING_IDLE_MS = 4_000;

/** Minimum milliseconds between consecutive shown suggestions. */
const SUGGESTION_COOLDOWN_MS = 10_000;

/** Priority for first unmet dimension (excludes values_aligned). */
export const DRAFT_ASSIST_DIMENSION_PRIORITY: DimensionKey[] = [DimensionKey.Personal, DimensionKey.Contextual, DimensionKey.Specific, DimensionKey.Genuine];

const CONFIDENCE_UPGRADE_THRESHOLD = 0.5;

export type DraftAssistDimension = {
	key: DimensionKey;
	matches: boolean;
	reason?: string;
};

export type UseDraftAssistProps = {
	dimensions: DraftAssistDimension[];
	draftText: string;
	recipientIds: string[];
	/** When true, scoring is in flight — do not surface a new suggestion yet. */
	isScoring?: boolean;
	/** When false, the LLM call is gated — only fire when the user has opened the panel. */
	isOpen?: boolean;
	/**
	 * Override the typing idle delay in milliseconds.
	 * @internal For testing only — do not pass in production.
	 */
	_typingIdleMs?: number;
	/**
	 * Override the suggestion cooldown in milliseconds.
	 * @internal For testing only — do not pass in production.
	 */
	_cooldownMs?: number;
};

export type CurrentDraftSuggestion = {
	text: string;
	dimensionKey: DimensionKey;
};

export type DraftAssistProp = {
	suggestion?: string;
	dimensionKey?: string;
	isLoading?: boolean;
	onAdd?: (text: string) => void;
	onDismiss?: () => void;
};

export function pickFirstUnmetDraftAssistDimension(dimensions: DraftAssistDimension[], suppressedDimensions: Set<string>): DimensionKey | null {
	const byKey = new Map(dimensions.map((d) => [d.key, d]));
	for (const key of DRAFT_ASSIST_DIMENSION_PRIORITY) {
		const dim = byKey.get(key);
		if (!dim || dim.matches) {
			continue;
		}
		if (suppressedDimensions.has(key)) {
			continue;
		}
		return key;
	}
	return null;
}

export function useDraftAssist({
	dimensions,
	draftText,
	recipientIds,
	isScoring = false,
	isOpen = true,
	_typingIdleMs = TYPING_IDLE_MS,
	_cooldownMs = SUGGESTION_COOLDOWN_MS,
}: UseDraftAssistProps) {
	const enabled = useFeatureFlag('bizy-draft-assist');
	const { track } = useAnalytics();

	const [suppressedDimensions, setSuppressedDimensions] = useState<Set<string>>(() => new Set());
	const [suggestionsShownCount, setSuggestionsShownCount] = useState(0);
	const [currentSuggestion, setCurrentSuggestion] = useState<CurrentDraftSuggestion | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	/** After editing the draft, wait until scoring finishes before another suggestion. */
	const [pendingScoreAfterEdit, setPendingScoreAfterEdit] = useState(false);
	/** After dismiss, wait until the draft text changes (then wait for score via pendingScoreAfterEdit). */
	const [pendingDraftChangeAfterDismiss, setPendingDraftChangeAfterDismiss] = useState(false);

	/**
	 * Explicit boolean tracking whether the user has been idle for at least _typingIdleMs.
	 * Set to false immediately whenever draftText changes, and set to true only after the
	 * idle timer fires. This correctly handles undo (B→A) where the reverted text matches
	 * a prior `lastIdleDraft` value — a string-equality check would incorrectly report idle
	 * since the timer has not yet fired for the reverted state.
	 */
	const [isIdle, setIsIdle] = useState(true);
	const typingIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	/** Ref tracking the last draftText seen by the idle effect to skip the initial mount. */
	const prevDraftForIdleRef = useRef(draftText);

	/** isTypingIdle is true when the dedicated idle boolean confirms the user has paused. */
	const isTypingIdle = isIdle;

	/** True during the cooldown window after a suggestion is shown (prevents back-to-back interruptions). */
	const [cooldownActive, setCooldownActive] = useState(false);

	const suppressedRef = useRef(suppressedDimensions);
	suppressedRef.current = suppressedDimensions;

	const dimensionsRef = useRef(dimensions);
	dimensionsRef.current = dimensions;

	const dimensionsKey = useMemo(
		() =>
			dimensions
				.map((d) => `${d.key}:${d.matches ? '1' : '0'}`)
				.sort()
				.join('|'),
		[dimensions]
	);

	// Stable primitive key so the fetch effect doesn't re-trigger on each render
	// when the caller passes a new array literal with the same IDs (e.g. in tests).
	const recipientIdsKey = useMemo(() => recipientIds.join(','), [recipientIds]);

	const suggestionDraftTextRef = useRef<string | null>(null);
	const requestGenerationRef = useRef(0);
	const prevIsScoringRef = useRef(isScoring);
	const prevDraftTextRef = useRef(draftText);
	const prevIsHintVisibleRef = useRef(false);
	/** Dimensions snapshot when we started waiting for a score after an edit/dismiss flow — clear pending when this changes. */
	const pendingScoreBaselineRef = useRef<string | null>(null);
	/** Updated after effects; draft-change effect reads previous render's dimensionsKey via this ref. */
	const prevDimensionsKeyRef = useRef(dimensionsKey);

	/** Typing idle gate: on each draftText change, mark not idle and restart the idle timer. */
	useEffect(() => {
		if (prevDraftForIdleRef.current === draftText) return;
		prevDraftForIdleRef.current = draftText;

		setIsIdle(false);
		if (typingIdleTimerRef.current) clearTimeout(typingIdleTimerRef.current);
		typingIdleTimerRef.current = setTimeout(() => setIsIdle(true), _typingIdleMs);

		return () => {
			if (typingIdleTimerRef.current) {
				clearTimeout(typingIdleTimerRef.current);
				typingIdleTimerRef.current = null;
			}
		};
	}, [draftText, _typingIdleMs]);

	/** Cooldown: after a suggestion is shown, block the next one for _cooldownMs. */
	useEffect(() => {
		if (!cooldownActive) return;
		const timer = setTimeout(() => setCooldownActive(false), _cooldownMs);
		return () => clearTimeout(timer);
	}, [cooldownActive, _cooldownMs]);

	/** When scoring completes, clear the "wait for score after edit" gate. */
	useEffect(() => {
		if (prevIsScoringRef.current && !isScoring) {
			setPendingScoreAfterEdit(false);
		}
		prevIsScoringRef.current = isScoring;
	}, [isScoring]);

	/** If the score result changes while we're waiting for a fresh score, allow suggestions again. */
	useEffect(() => {
		if (!pendingScoreAfterEdit) {
			return;
		}
		if (pendingScoreBaselineRef.current === null) {
			return;
		}
		if (dimensionsKey !== pendingScoreBaselineRef.current) {
			setPendingScoreAfterEdit(false);
		}
	}, [dimensionsKey, pendingScoreAfterEdit]);

	/** Short draft: scoring won't run — do not block forever. */
	useEffect(() => {
		if (!pendingScoreAfterEdit) {
			return;
		}
		if (cleanedDraftLength(draftText) < MIN_CLEANED_DRAFT_LENGTH) {
			setPendingScoreAfterEdit(false);
		}
	}, [draftText, pendingScoreAfterEdit]);

	/** Draft text changed: clear visible suggestion, set gates per product rules. */
	useEffect(() => {
		if (prevDraftTextRef.current === draftText) {
			return;
		}
		prevDraftTextRef.current = draftText;

		if (pendingDraftChangeAfterDismiss) {
			setPendingDraftChangeAfterDismiss(false);
			pendingScoreBaselineRef.current = prevDimensionsKeyRef.current;
			setPendingScoreAfterEdit(true);
			return;
		}

		if (currentSuggestion) {
			setCurrentSuggestion(null);
			setIsLoading(false);
			suggestionDraftTextRef.current = null;
		}
	}, [draftText, currentSuggestion, pendingDraftChangeAfterDismiss]);

	/** Discard if the prioritized unmet dimension changed while a suggestion was visible. */
	useEffect(() => {
		if (!currentSuggestion || isScoring) {
			return;
		}
		const first = pickFirstUnmetDraftAssistDimension(dimensions, suppressedRef.current);
		if (first !== currentSuggestion.dimensionKey) {
			setCurrentSuggestion(null);
			setIsLoading(false);
			suggestionDraftTextRef.current = null;
		}
	}, [dimensionsKey, dimensions, currentSuggestion, isScoring]);

	/** Fetch personalized suggestion when guards pass. */
	useEffect(() => {
		if (!enabled) {
			return;
		}
		if (!isOpen) {
			return;
		}
		if (!isTypingIdle) {
			return;
		}
		if (cooldownActive) {
			return;
		}
		if (isScoring) {
			return;
		}
		if (pendingScoreAfterEdit) {
			return;
		}
		if (pendingDraftChangeAfterDismiss) {
			return;
		}
		if (currentSuggestion) {
			return;
		}
		if (suggestionsShownCount >= MAX_SUGGESTIONS_PER_SESSION) {
			return;
		}
		if (recipientIds.length === 0) {
			return;
		}
		if (cleanedDraftLength(draftText) < MIN_CLEANED_DRAFT_LENGTH) {
			return;
		}

		const dimensionKey = pickFirstUnmetDraftAssistDimension(dimensions, suppressedRef.current);
		if (!dimensionKey) {
			return;
		}

		suggestionDraftTextRef.current = draftText;
		setIsLoading(true);

		const generation = ++requestGenerationRef.current;
		const draftSnapshot = draftText;
		const idsSnapshot = [...recipientIds];

		loadDraftAssist({
			draftText: draftSnapshot,
			dimensionKey,
			recipientIds: idsSnapshot,
		})
			.then((data) => {
				if (generation !== requestGenerationRef.current) {
					return;
				}
				if (data.confidence < CONFIDENCE_UPGRADE_THRESHOLD) {
					return;
				}
				if (suggestionDraftTextRef.current !== draftSnapshot) {
					return;
				}
				// Use the dimension the backend actually targeted, not just what we requested.
				// These can differ if the backend reroutes to a more appropriate dimension.
				const resolvedDimensionKey = data.targetDimension;
				const stillUnmet = pickFirstUnmetDraftAssistDimension(dimensionsRef.current, suppressedRef.current);
				if (stillUnmet !== resolvedDimensionKey) {
					return;
				}

				track(AnalyticsEvent.GratitudeGauge.DraftAssistPersonalizedShown, {
					dimensionKey: resolvedDimensionKey,
				});

				setCooldownActive(true);
				setSuggestionsShownCount((c) => c + 1);
				setCurrentSuggestion({
					text: data.suggestion,
					dimensionKey: resolvedDimensionKey,
				});
			})
			.catch(() => {
				/* no suggestion to show */
			})
			.finally(() => {
				if (generation === requestGenerationRef.current) {
					setIsLoading(false);
				}
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- recipientIds is intentionally replaced by recipientIdsKey (a stable string) to prevent re-triggering on new array references with identical content
	}, [
		enabled,
		isOpen,
		isTypingIdle,
		cooldownActive,
		isScoring,
		pendingScoreAfterEdit,
		pendingDraftChangeAfterDismiss,
		currentSuggestion,
		suggestionsShownCount,
		draftText,
		recipientIdsKey,
		dimensions,
		dimensionsKey,
		track,
	]);

	/** Keep ref in sync after other effects so draft-change reads the prior render's dimensionsKey. */
	useEffect(() => {
		prevDimensionsKeyRef.current = dimensionsKey;
	}, [dimensionsKey]);

	// Only signal that Bizy can help after scoring has actually run (draft long enough, not scoring).
	// Computed before the early return so the hint-shown effect can run unconditionally.
	// recipientIdsKey guard mirrors the fetch effect — no recipients means no suggestion can be fetched.
	// pendingDraftChangeAfterDismiss / pendingScoreAfterEdit guards prevent the hint from appearing
	// in the brief window after dismiss or accept when the fetch effect is still blocked.
	// isTypingIdle (an explicit boolean set false on every draft change, true only when the idle
	// timer fires) prevents the hint from appearing while the user is actively composing.
	// cooldownActive prevents the hint from appearing during the post-suggestion cooldown window.
	const hasUnmetDimension = useMemo(
		() =>
			enabled &&
			isTypingIdle &&
			!cooldownActive &&
			!isScoring &&
			!pendingDraftChangeAfterDismiss &&
			!pendingScoreAfterEdit &&
			recipientIdsKey.length > 0 &&
			suggestionsShownCount < MAX_SUGGESTIONS_PER_SESSION &&
			cleanedDraftLength(draftText) >= MIN_CLEANED_DRAFT_LENGTH &&
			pickFirstUnmetDraftAssistDimension(dimensions, suppressedDimensions) !== null,
		[
			enabled,
			isTypingIdle,
			cooldownActive,
			isScoring,
			pendingDraftChangeAfterDismiss,
			pendingScoreAfterEdit,
			recipientIdsKey,
			suggestionsShownCount,
			draftText,
			dimensions,
			suppressedDimensions,
		]
	);

	/** The unmet dimension the hint bubble refers to — used to include dimensionKey in the hint-clicked event. */
	const hintDimensionKey = useMemo(
		() => (hasUnmetDimension ? pickFirstUnmetDraftAssistDimension(dimensions, suppressedDimensions) : null),
		[hasUnmetDimension, dimensions, suppressedDimensions]
	);

	/** Track when the hint bubble first becomes visible (false → true transition of isHintVisible).
	 * The popover renders only when hasUnmetDimension && !isOpen, so both conditions must hold. */
	useEffect(() => {
		const isHintVisible = hasUnmetDimension && !isOpen;
		if (!isHintVisible || prevIsHintVisibleRef.current) {
			prevIsHintVisibleRef.current = isHintVisible;
			return;
		}
		const dimensionKey = pickFirstUnmetDraftAssistDimension(dimensions, suppressedDimensions);
		if (dimensionKey) {
			track(AnalyticsEvent.GratitudeGauge.DraftAssistHintShown, { dimensionKey });
		}
		prevIsHintVisibleRef.current = isHintVisible;
	}, [hasUnmetDimension, isOpen, dimensions, suppressedDimensions, track]);

	const accept = useCallback(() => {
		if (!currentSuggestion) {
			return;
		}
		track(AnalyticsEvent.GratitudeGauge.DraftAssistAccepted, {
			dimensionKey: currentSuggestion.dimensionKey,
		});
		pendingScoreBaselineRef.current = dimensionsKey;
		setPendingScoreAfterEdit(true);
		setCurrentSuggestion(null);
		setIsLoading(false);
		suggestionDraftTextRef.current = null;
	}, [currentSuggestion, dimensionsKey, track]);

	const dismiss = useCallback(() => {
		if (!currentSuggestion) {
			return;
		}
		const key = currentSuggestion.dimensionKey;
		track(AnalyticsEvent.GratitudeGauge.DraftAssistDismissed, {
			dimensionKey: key,
		});
		setSuppressedDimensions((prev) => new Set(prev).add(key));
		setCurrentSuggestion(null);
		setIsLoading(false);
		suggestionDraftTextRef.current = null;
		setPendingDraftChangeAfterDismiss(true);
	}, [currentSuggestion, track]);

	if (!enabled) {
		return {
			suggestion: null,
			isLoading: false,
			dimensionKey: null,
			hintDimensionKey: null,
			hasUnmetDimension: false,
			accept: NOOP,
			dismiss: NOOP,
		};
	}

	return {
		suggestion: currentSuggestion?.text ?? null,
		isLoading,
		dimensionKey: currentSuggestion?.dimensionKey ?? null,
		hintDimensionKey,
		hasUnmetDimension,
		accept,
		dismiss,
	};
}
